import React, { useMemo } from 'react'
import { defineMessages, FormattedMessage, MessageDescriptor } from 'react-intl'

import { useTheme } from 'styled-components/native'

import { ClientFiltersCheckboxSection } from './ClientFiltersCheckboxSection'
import { BodyText } from '../../../atoms/bodyText/BodyText'

export enum ActivityStatus {
  COMPLETE = 'complete',
  PENDING = 'pending',
  INCOMPLETE = 'incomplete',
  NOT_SENT = 'not_sent',
}

export type ClientFiltersActivityStatusSectionProps = {
  values: ActivityStatus[]
  onChange: (values: ActivityStatus[]) => void
}

export const ClientFiltersActivityStatusSection: React.FC<ClientFiltersActivityStatusSectionProps> = ({
  values,
  onChange,
}) => {
  const theme = useTheme()
  const filterNames: Record<ActivityStatus, MessageDescriptor> = useMemo(
    () =>
      defineMessages({
        [ActivityStatus.COMPLETE]: {
          defaultMessage: 'Complete',
          description: 'Label for filtering conversations by completed activity status',
        },
        [ActivityStatus.PENDING]: {
          defaultMessage: 'Pending <secondary>(more than 48 hours prior to next session)</secondary>',
          description: 'Label for filtering conversations by pending activity status',
          values: {
            secondary: (chunks: string) => <BodyText color={theme.colors.textSecondary} text={chunks} />,
          },
        },
        [ActivityStatus.INCOMPLETE]: {
          defaultMessage: 'Incomplete <secondary>(less than 48 hours before the session)</secondary>',
          description: 'Label for filtering conversations by incomplete activity status',
          values: {
            secondary: (chunks: string) => <BodyText color={theme.colors.textSecondary} text={chunks} />,
          },
        },
        [ActivityStatus.NOT_SENT]: {
          defaultMessage: 'None sent',
          description: 'Label for filtering conversations by not sent activity status',
        },
      }),
    [theme],
  )

  return (
    <ClientFiltersCheckboxSection
      title={<FormattedMessage defaultMessage='Activity status' description='Activity status filter section' />}
      items={Object.values(ActivityStatus).map((value) => ({
        id: value,
        text: <FormattedMessage {...filterNames[value]} />,
      }))}
      values={values}
      onChange={onChange}
      name='activityStatus'
    />
  )
}
